import { computed, readonly, ref, type ComputedRef, type Ref } from 'vue';

// The theme (ADR 0010): auto, dark or light, chosen per device. The choice is kept in
// localStorage as itc:theme ('dark' or 'light'; no key is auto). A forced theme is
// html[data-theme]; auto leaves the attribute off, and tokens.css follows
// prefers-color-scheme. apps/web/public/theme-init.js applies the stored choice before
// first paint, by the same rules.

export type ThemeChoice = 'auto' | 'dark' | 'light';
export type Theme = 'dark' | 'light';
export const THEME_KEY = 'itc:theme';

export interface ThemeEnv {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined;
  matchMedia?: ((query: string) => Pick<MediaQueryList, 'matches' | 'addEventListener'>) | undefined;
  root?: Pick<HTMLElement, 'dataset'> | undefined;
}

export interface ThemeHandle {
  /** What the person picked. */
  choice: Readonly<Ref<ThemeChoice>>;
  /** The theme on screen: the choice, or the OS's for auto. */
  effective: ComputedRef<Theme>;
  set: (choice: ThemeChoice) => void;
}

const isForced = (value: unknown): value is Theme => value === 'dark' || value === 'light';

function read(storage: ThemeEnv['storage']): ThemeChoice {
  try {
    const stored = storage?.getItem(THEME_KEY);
    return isForced(stored) ? stored : 'auto';
  } catch {
    return 'auto'; // Storage can be blocked (private modes); auto is always safe.
  }
}

/** A theme handle over the given environment. useTheme() is this over the browser's, shared. */
export function createTheme(env: ThemeEnv): ThemeHandle {
  const choice = ref<ThemeChoice>(read(env.storage));
  const light = env.matchMedia?.('(prefers-color-scheme: light)');
  const systemLight = ref(light?.matches ?? false);
  light?.addEventListener('change', (event) => {
    systemLight.value = event.matches;
  });

  const apply = (value: ThemeChoice): void => {
    if (env.root === undefined) return;
    if (value === 'auto') delete env.root.dataset['theme'];
    else env.root.dataset['theme'] = value;
  };
  apply(choice.value);

  return {
    choice: readonly(choice),
    effective: computed(() => (choice.value === 'auto' ? (systemLight.value ? 'light' : 'dark') : choice.value)),
    set(value) {
      choice.value = value;
      apply(value);
      try {
        if (value === 'auto') env.storage?.removeItem(THEME_KEY);
        else env.storage?.setItem(THEME_KEY, value);
      } catch {
        // Not remembered; the choice still applies to this page.
      }
    },
  };
}

let shared: ThemeHandle | undefined;

/** The device's theme choice, shared by every caller (the user menu, /settings). */
export function useTheme(): ThemeHandle {
  if (shared === undefined) {
    const browser = typeof window !== 'undefined';
    shared = createTheme(
      browser
        ? {
            storage: (() => {
              try {
                return window.localStorage;
              } catch {
                return undefined;
              }
            })(),
            matchMedia: window.matchMedia.bind(window),
            root: document.documentElement,
          }
        : {},
    );
  }
  return shared;
}
